import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AlertsCard } from '@/components/organization/AlertsCard'
import { DangerZone } from '@/components/organization/DangerZone'
import { GeneralCard } from '@/components/organization/GeneralCard'
import { MembersCard } from '@/components/organization/MembersCard'
import { PageHeader } from '@/components/PageHeader'
import { organization } from '@/lib/auth-client'
import { personalOrgFor } from '@/lib/org/org'

export const Route = createFileRoute('/_authenticated/organization')({
  component: OrganizationPage,
})

function OrganizationPage() {
  // Org and viewer both come from the route context, so this page no longer
  // waits on a client-side active-org request (and a session request) before it
  // can even ask for the member list.
  const { activeOrg, user } = Route.useRouteContext()
  const orgId = activeOrg?.id ?? null
  const myUserId = user.id
  // A personal org is re-provisioned on next load, so deleting it is pointless —
  // hide the delete action for it (the server fn also refuses).
  const isPersonal = activeOrg
    ? activeOrg.slug === personalOrgFor(user).slug
    : false

  if (!orgId) {
    return (
      <PageHeader
        title="Organization"
        description="No organization selected."
      />
    )
  }

  return (
    <OrgContent
      orgId={orgId}
      myUserId={myUserId}
      activeName={activeOrg?.name ?? ''}
      isPersonal={isPersonal}
    />
  )
}

function OrgContent({
  orgId,
  myUserId,
  activeName,
  isPersonal,
}: {
  orgId: string
  myUserId: string | null
  activeName: string
  isPersonal: boolean
}) {
  // Same query key as MembersCard (TanStack Query dedupes), so this adds no
  // extra request — it just derives the viewer's role to gate the management UI
  // (owner/admin only). Members get a read-only view.
  const membersQuery = useQuery({
    queryKey: ['org-members', orgId],
    queryFn: async () => {
      const { data, error } = await organization.listMembers({
        query: { organizationId: orgId },
      })
      if (error) throw new Error(error.message ?? 'Failed to load members')
      return data
    },
  })
  const members = membersQuery.data?.members ?? []
  const myRole = members.find((m) => m.userId === myUserId)?.role
  const canManage = myRole === 'owner' || myRole === 'admin'
  const isOwner = myRole === 'owner'

  const roleLabel =
    myRole === 'owner'
      ? 'an owner'
      : myRole === 'admin'
        ? 'an admin'
        : myRole === 'member'
          ? 'a member'
          : myRole
  // A sentence that explains the page, like every other page header — the org
  // name is already in the switcher and breadcrumb. The caller's own role stays
  // because it's what explains which controls they can see.
  const description = myRole
    ? `Manage who can access this organization. You are ${roleLabel}.`
    : 'Manage who can access this organization.'

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Organization" description={description} />

      {canManage ? (
        <GeneralCard orgId={orgId} currentName={activeName} />
      ) : null}

      {canManage ? <AlertsCard orgId={orgId} /> : null}

      <MembersCard
        orgId={orgId}
        myUserId={myUserId}
        orgName={activeName}
        canManage={canManage}
      />

      <DangerZone
        orgId={orgId}
        orgName={activeName}
        myUserId={myUserId}
        isOwner={isOwner}
        isPersonal={isPersonal}
        members={members}
      />
    </div>
  )
}
